import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ProjectCard } from '../components/ProjectCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['All'];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const filteredProjects = activeCategory === 'All'
    ? projects
    : projects.filter(p => p.category === activeCategory);

  return (
    <div data-testid="projects-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="projects-hero">
        <div className="max-w-4xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-dm-sans text-xs text-stone-400 tracking-wide uppercase mb-4 block"
          >
            Our Work
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-stone-900 leading-tight mb-8"
          >
            Selected
            <br />
            <span className="italic">projects</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-dm-sans text-lg text-stone-500 leading-relaxed max-w-2xl"
          >
            A curated collection of our work spanning residential, commercial, and interior design.
          </motion.p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-wrap gap-4"
          data-testid="project-filters"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              data-testid={`filter-${category.toLowerCase()}`}
              className={`font-manrope text-xs uppercase tracking-[0.2em] font-medium px-6 py-3 rounded-full border transition-colors duration-300 ${
                activeCategory === category
                  ? 'bg-stone-900 text-white border-stone-900'
                  : 'bg-transparent text-stone-600 border-stone-300 hover:border-stone-900'
              }`}
            >
              {category}
            </button>
          ))}
        </motion.div>
      </section>

      {/* Projects Grid */}
      <section className="py-12 md:py-16 px-6 md:px-12 lg:px-24" data-testid="projects-grid">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i}>
                <div className="bg-stone-100 animate-pulse aspect-[16/10]" />
                <div className="mt-6 space-y-2">
                  <div className="h-6 bg-stone-100 animate-pulse w-3/4" />
                  <div className="h-4 bg-stone-100 animate-pulse w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16"
            >
              {filteredProjects.length > 0 ? (
                filteredProjects.map((project, index) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    index={index}
                  />
                ))
              ) : (
                <div className="col-span-2 text-center py-16">
                  <p className="font-dm-sans text-stone-400">
                    No projects found in this category.
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </section>

      {/* Bottom Spacing */}
      <div className="py-16 md:py-24" />
    </div>
  );
}
